import { useMediaQuery } from 'react-responsive';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import Typography from 'components/ui/Typography';
import Flex from 'components/ui/Flex';
import ArrowRight from 'components/icons/ArrowRight';
import { breakPoint, palette } from 'shared/styles';

const patents = [
  {
    number: '10-2784757',
    name: 'AI 및 딥러닝 기반의 화장품 인증관리 시스템',
    date: '2025.03.17',
  },
  {
    number: '10-2768601',
    name: '외용제품의 판매에 사용할 수 있는 소구 문구를 정하여 제공하는 방법과 그 방법을 위한 장치',
    date: '2025.03.17',
  },
  {
    number: '10-2770585',
    name: '글로벌 화장품 인증 보조 시스템',
    date: '2025.02.17',
  },
  {
    number: '10-2770595',
    name: '화장품 규제변동 예측 장치 및 방법',
    date: '2025.02.17',
  },
  {
    number: '10-2770495',
    name: '클라이언트 장치에 세션에 대한 정보를 제시하는 방법 및 그 방법을 위한 장치',
    date: '2025.02.14',
  },
  {
    number: '10-2738911',
    name: '화장품 국가 인증 시스템 및 방법',
    date: '2024.12.02',
  },
  {
    number: '10-2714234',
    name: '외용제품의 매매에 요구되는 품질관련 증빙서류를 구비하는 방법 및 장치',
    date: '2024.09.30',
  },
  {
    number: '10-2692221',
    name: '인체에 외용되는 제품의 인증관련 정보를 검사하고 그 검사결과를 제공하는 방법 및 장치',
    date: '2024.08.01',
  },
  {
    number: '10-2692087',
    name: '인체에 외용되는 제품의 승인 신청을 국가별로 구분하여 통합처리하는 방법 및 장치',
    date: '2024.07.31',
  },
  {
    number: '10-2650226',
    name: '인체에 외용되는 제품의 인증을 위한 정보를 수신받아 구성하는 방법 및 장치',
    date: '2024.03.18',
  },
];

const SectionD = () => {
  const isLargeDevice = useMediaQuery({ query: breakPoint.LARGE_DEVICE });
  const isMobile = useMediaQuery({ query: breakPoint.MOBILE });

  const handleClickLink = () => {
    window.open('https://medium.com/cdri', '_blank');
  };

  return (
    <Container>
      <PatentList>
        <Typography.Title
          type="h2"
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '40px',
            fontSize: isMobile ? '24px' : '48px',
          }}
        >
          CDRI PATENT
        </Typography.Title>
        <Table isLargeDevice={isLargeDevice}>
          {!isMobile && (
            <thead>
              <tr>
                <th>등록번호</th>
                <th>특허명칭</th>
                <th>등록일자</th>
              </tr>
            </thead>
          )}
          <tbody>
            {patents.map((patent) => (
              <tr key={patent.number}>
                <td className="patentNumber">{patent.number}</td>
                <td className="patentName">{patent.name}</td>
                <td className="patentDate">{patent.date}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <ImageWrapper>
          <BlogImage
            src={
              isMobile
                ? 'https://cdri.s3.ap-northeast-2.amazonaws.com/assets/cdri_blog_mobile.png'
                : 'https://cdri.s3.ap-northeast-2.amazonaws.com/assets/cdri_blog.png'
            }
            alt="CDRI 기술 블로그 배경사진"
          />
          <div
            style={{
              position: 'absolute',
              top: isMobile ? '32px' : '50%',
              left: isMobile ? '32px' : '0',
              transform: isMobile ? '' : 'translate(80px, -50%)',
              display: 'flex',
              flexFlow: 'column',
              rowGap: isMobile ? '24px' : '42px',
            }}
          >
            <BlogTitle>CDRI 기술 블로그</BlogTitle>
            <Link
              onClick={handleClickLink}
              className="link"
              isMobile={isMobile}
            >
              <Flex align="center" gap={8}>
                <Typography.Text
                  css={css`
                    font-size: 16px;
                    font-weight: 600;

                    ${isMobile &&
                    css`
                      font-size: 14px;
                      line-height: 22px;
                    `}
                  `}
                >
                  블로그 보러가기
                </Typography.Text>
                <ArrowRight
                  css={css`
                    ${isMobile &&
                    css`
                      width: 16px;
                      height: 16px;
                    `}
                  `}
                />
              </Flex>
            </Link>
          </div>
        </ImageWrapper>
      </PatentList>
    </Container>
  );
};

export default SectionD;

const Container = styled.section`
  position: relative;
  padding: 200px 0 !important;

  @media screen and ${breakPoint.MOBILE} {
    padding: 80px 24px !important;
  }
  @media screen and ${breakPoint.TABLET_TO_LARGE_DEVICE} {
    padding: 120px 24px !important;
  }
`;

const PatentList = styled.section`
  width: 1200px;
  margin: 0 auto;

  @media screen and ${breakPoint.LARGE_DEVICE} {
    width: 100%;
  }
`;

const Table = styled.table<{ isLargeDevice: boolean }>`
  ${({ isLargeDevice }) => css`
    width: 100%;
    max-width: 1200px;
    margin-bottom: 200px;
    font-size: 20px;
    font-weight: 500;
    line-height: 32px;

    @media screen and ${breakPoint.MOBILE} {
      margin-bottom: 80px;
    }

    thead {
      & > tr {
        height: 76px;
        border-top: 1px solid ${palette.BLACK};

        & > th {
          color: ${palette.BLACK};
        }
      }
    }

    tbody {
      & > tr {
        height: 80px;
        @media screen and ${breakPoint.LARGE_DEVICE} {
          padding: 16px 0;
          height: fit-content;
        }

        @media screen and ${breakPoint.MOBILE} {
          height: fit-content;

          :first-of-type {
            border-top: 1px solid ${palette.BLACK};
          }
        }
      }
    }

    tr {
      display: grid;
      grid-template-columns: ${isLargeDevice
        ? '160px 1fr 160px'
        : '160px 720px 160px'};
      column-gap: 40px;
      justify-content: center;
      align-items: center;
      border-bottom: 1px solid ${palette.LIGHT_GREY3};

      @media screen and ${breakPoint.MOBILE} {
        display: flex;
        flex-flow: column;
        justify-content: flex-start;
        align-items: start;
      }
    }

    th,
    td {
      text-align: center;
      vertical-align: middle;
    }

    .patentNumber {
      color: ${palette.PRIMARY};

      @media screen and ${breakPoint.MOBILE} {
        font-size: 16px;
      }
    }

    .patentName {
      font-weight: 600;
      text-align: left;

      @media screen and ${breakPoint.MOBILE} {
        font-size: 16px;
      }
    }

    .patentDate {
      color: ${palette.BLUE_GREY};

      @media screen and ${breakPoint.MOBILE} {
        font-size: 16px;
      }
    }
  `}
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  width: 1200px;
  /* height: 322px; */
  border-radius: 12px;
  overflow: hidden;
  /* padding: 80px 100px; */

  @media screen and ${breakPoint.LARGE_DEVICE} {
    width: 100%;
  }

  @media screen and ${breakPoint.MOBILE} {
  }
`;

const BlogImage = styled.img``;

const BlogTitle = styled.span`
  font-size: 40px;
  font-weight: 700;
  line-height: 64px;
  z-index: 1;

  @media screen and ${breakPoint.MOBILE} {
    font-size: 24px;
    line-height: 34px;
  }
`;

const Link = styled.div<{ isMobile: boolean }>`
  position: relative;
  cursor: pointer;
  width: fit-content;
  padding: 14px 24px;
  box-shadow: 0 0 0 1px ${palette.BLACK} inset;
  border-radius: 31px;
  overflow: hidden;

  span,
  svg {
    position: relative;
    z-index: 1;
  }

  span {
    transition: color 0.3s ease-in-out;
  }

  svg {
    width: 24px;

    path {
      fill: ${palette.BLACK};
      transition: fill 0.3s ease-in-out;
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background-color: ${palette.BLACK};
    z-index: 0;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease-in-out;
  }

  &:hover {
    span {
      color: ${palette.WHITE};
    }

    svg {
      path {
        fill: ${palette.WHITE};
      }
    }

    &:after {
      transform: scaleX(1);
    }
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: 13px 18px;
    `}
`;
